import {DocumentTextIcon, ExclamationTriangleIcon, LockClosedIcon} from '@heroicons/react/24/outline'
import {cn} from '../../../../../../lib/utils.ts'
import {fieldVariants, fieldLabelClasses} from '../../../../../../components/general/fieldStyles.ts'
import {DEFAULT_INVOICE_NUMBER_PREFIX, MAX_INVOICE_NUMBER_PREFIX_LENGTH} from '../types.ts'
import {resolveInvoiceNumberPreview} from '../utils/invoiceLines.ts'
import {SalesInvoiceNumbering} from '../salesInvoiceApi.ts'

type InvoiceNumberSectionProps = {
    invoiceDate: string
    prefix: string
    onPrefixChange: (prefix: string) => void
    numbering: SalesInvoiceNumbering | null
    isLoadingNumbering: boolean
}

export function InvoiceNumberSection({
    invoiceDate,
    prefix,
    onPrefixChange,
    numbering,
    isLoadingNumbering,
}: InvoiceNumberSectionProps) {
    const {effectivePrefix, isNumberingCurrent, nextSequence, numberPreview} =
        resolveInvoiceNumberPreview(invoiceDate, prefix, numbering)

    return (
        <section>
            <h2 className="text-xl font-bold">Factuurnummer</h2>
            <p className="text-xs text-gray-600 mt-1 mb-4">
                Factuurnummers moeten wettelijk opeenvolgend zijn. Het formaat staat daarom vast:
                prefix, jaar en volgnummer.
            </p>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className={cn(fieldLabelClasses, 'mb-1')} htmlFor="invoice-prefix-input">Prefix</label>
                        <input
                            id="invoice-prefix-input"
                            className={cn(fieldVariants())}
                            value={prefix}
                            maxLength={MAX_INVOICE_NUMBER_PREFIX_LENGTH}
                            placeholder={DEFAULT_INVOICE_NUMBER_PREFIX}
                            onChange={(e) => onPrefixChange(e.target.value)}
                        />
                        <p className="mt-1 text-xs text-gray-500">
                            Een andere prefix start een eigen reeks. Al verstuurde nummers blijven staan.
                        </p>
                    </div>
                    <div>
                        <label className={cn(fieldLabelClasses, 'mb-1')}>Volgnummer</label>
                        <div className={cn(fieldVariants(), 'flex items-center gap-2 bg-gray-100 text-gray-500')}>
                            <LockClosedIcon className="h-4 w-4 flex-shrink-0"/>
                            {nextSequence !== null ? String(nextSequence).padStart(5, '0') : 'Automatisch'}
                        </div>
                        <p className="mt-1 text-xs text-gray-500">
                            Telt per jaar automatisch door binnen deze prefix.
                        </p>
                    </div>
                </div>
                {isNumberingCurrent && numbering?.sequence_exhausted ? (
                    <div className="mt-4 flex items-start gap-2 rounded-md bg-yellow-50 border border-yellow-300 px-3 py-2.5 text-sm">
                        <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 flex-shrink-0"/>
                        <span className="text-yellow-800">
                            Er zijn dit jaar geen volgnummers meer beschikbaar voor prefix{' '}
                            <span className="font-semibold">{effectivePrefix}</span>. Kies een andere
                            prefix om verder te kunnen factureren.
                        </span>
                    </div>
                ) : (
                    <div className="mt-4 flex items-center gap-2 rounded-md bg-superliteprimary px-3 py-2.5 text-sm">
                        <DocumentTextIcon className="h-5 w-5 text-primary flex-shrink-0"/>
                        <span className="text-gray-700">
                            {isLoadingNumbering ? 'Nummer ophalen…' : 'Deze factuur krijgt nummer'}
                        </span>
                        {!isLoadingNumbering && <span className="font-bold text-primary">{numberPreview}</span>}
                    </div>
                )}
            </div>
        </section>
    )
}
